import { useMemo } from 'react';
import { MenuItem, Tab } from '@mui/material';
import { useAlerts } from 'contexts/alertsContext';
import { useAuth } from 'contexts/authContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { navbarRoutesList } from 'root/pathnames';
import { User } from 'types/user';
import { useRoutesMatch } from 'utils/hooks';
import { getIcon } from 'utils/icons.utils';
import { createBadgeTab } from './createBadgeTab';
import { createMenuTab } from './createMenuTab';
import { TabsStyled } from './Navbar.styles';
import { getDefaultNavOptions } from './navOptions';

interface Props {
  user: User;
}

export const NavTabsDefault = ({ user }: Props) => {
  const { i18n, t } = useTranslation('menu');
  const { hasViewPermission, logoutUser } = useAuth();

  const [navItems, navMenus] = useMemo(
    () => getDefaultNavOptions(i18n),
    [i18n],
  );

  const navOptionsList = useMemo(
    () => [
      navMenus.client,
      navItems.offers,
      navItems.jobs,
      navItems.invoices,
      navItems.rates,
      navItems.reports,
      navMenus.files,
      navItems.transportCloud,
    ],
    [navItems, navMenus],
  );

  const menusIds = useMemo(
    () =>
      Object.values(navMenus).reduce<{
        [k: string]: string;
      }>((acc, { menuItems, value: menuId }) => {
        const menuIds = menuItems?.reduce(
          (acc2, { value: path }) => ({ ...acc2, [path]: menuId }),
          {},
        );

        return { ...acc, ...menuIds };
      }, {}),
    [navMenus],
  );

  const routeMatch = useRoutesMatch(navbarRoutesList);

  const currentTab = useMemo(() => {
    const matchedPath = routeMatch?.pattern.path;
    return matchedPath ? menusIds[matchedPath] || matchedPath : undefined;
  }, [routeMatch, menusIds]);

  return (
    <TabsStyled value={currentTab} variant='scrollable' scrollButtons='auto'>
      <Tab
        className='HomeTab'
        id={navItems.home.name}
        label={navItems.home.title}
        component={Link}
        to={navItems.home.url}
        value={navItems.home.value}
      />
      {navOptionsList.map((navOption) => {
        const { name, title, url, value, icon, requiredRoles, menuItems } =
          navOption;
        if (!hasViewPermission(requiredRoles)) return null;
        if (!menuItems) {
          return (
            <Tab
              key={name}
              id={name}
              label={title}
              component={Link}
              to={url}
              value={value}
              icon={getIcon(icon)}
              iconPosition='start'
            />
          );
        }
        return (
          <Tab
            key={name}
            id={name}
            label={title}
            component={createMenuTab(navOption)}
            value={value}
            icon={getIcon(icon)}
            iconPosition='start'
          />
        );
      })}
      <Tab disabled sx={{ maxWidth: 'none', flex: '1 1 0' }} value='spacer' />
      {hasViewPermission(navItems.calendar.requiredRoles) && (
        <Tab
          id={navItems.calendar.name}
          label={navItems.calendar.title}
          component={Link}
          to={navItems.calendar.url}
          value={navItems.calendar.value}
          icon={getIcon(navItems.calendar.icon)}
          iconPosition='start'
        />
      )}
      {hasViewPermission(navItems.alerts.requiredRoles) && (
        <Tab
          id={navItems.alerts.name}
          component={createBadgeTab(
            navItems.alerts.url,
            navItems.alerts.alertsContextKey,
          )}
          value={navItems.alerts.value}
          icon={getIcon(navItems.alerts.icon)}
          iconPosition='start'
        />
      )}
      <Tab
        id={navMenus.user.name}
        label={user.code}
        component={createMenuTab(navMenus.user, [
          () => (
            <MenuItem key='logout' onClick={() => logoutUser('logout')}>
              {t('logout', { ns: 'menu' })}
            </MenuItem>
          ),
        ])}
        value={navMenus.user.value}
        icon={getIcon(navMenus.user.icon)}
        iconPosition='start'
      />
    </TabsStyled>
  );
};
